import React, {useState, useEffect} from "react"
import PropTypes from "prop-types"
import {RouteComponentProps} from "react-router"
import {hangPromise} from "@irati/utils/promise"
import Loading from "@irati/ui/src/Loading"
import DealByUrlSlug from "@irati/deals/apollo/query/DealByUrlSlug"
import {Card, Col, Row} from "@irati/ui/src/Layout"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import FormattedMessage from "@irati/utils/lang"
import {Checkout, Deal} from "@irati/types"
import redemptionFlow from "@irati/checkout/utils/redemption"
import checkoutError, {CheckoutError} from "@irati/checkout/utils/redemption/helpers/checkoutError"
import authentication from "@irati/auth"
import tracking from "@irati-tracking/perks/perks"
import withPerkOrReward, {WithPerkOrReward} from "@irati-web/flexi-rewards/src/utils/withPerkOrReward"
import detailsToWebAddress from "@irati/checkout/utils/redemption/helpers/detailsToWebAddress"
import history from "@irati/utils/history"

import PaymentError from "../../components/DealRedemptionError/PaymentError"
import StockAssignmentError from "../../components/DealRedemptionError/StockAssignmentError"
import RedemptionError from "../../components/DealRedemptionError/RedemptionError"
import UnknownError from "../../components/DealRedemptionError/UnknownError"
import DealBanner from "../../components/DealBanner"
import RequireDealStatusOn from "../../components/RequireDealStatusOn"
import DealSuccessFinished from "../../components/DealSuccess/Finished"

interface Props extends RouteComponentProps<{urlSlug: string, checkoutId: string, paymentOrderId: string}>, WithPerkOrReward{
}

const getTrackingData = (reason: CheckoutError, paymentOrderId: string, checkout?: Checkout) => ({
  reason,
  user_id: authentication.user().uuid,
  tenant_id: authentication.userTenant().uuid,
  payment_id: checkout?.paymentOrder?.payments?.map((payment) => payment.uuid).join(","),
  order_id: paymentOrderId,
  amount: checkout?.paymentOrder?.amount,
  currency: checkout?.paymentOrder?.currency,
  provider: checkout?.paymentOrder?.payments?.map((payment) => payment.provider).join(","),
  country_code: checkout?.paymentOrder?.countryCode,
  deal_id: checkout?.basket?.items?.map((item) => item.deal?.uuid).join(","),
  deal_header: checkout?.basket?.items?.map((item) => item.deal?.header).join(",")
})

const DealRedemptionError: React.FC<Props> = (props, context) => {
  const {urlSlug, checkoutId, paymentOrderId} = props.match.params
  const [load, setLoad] = useState<Promise<any>>(hangPromise())
  const [error, setError] = useState(CheckoutError.UnknownError)

  useEffect(() => {
    async function fetchError() {
      const request = checkoutError(paymentOrderId, checkoutId)
      setLoad(request)
      const {reason, checkout} = await request
      tracking.checkoutFailure(getTrackingData(reason, paymentOrderId, checkout))
      setError(reason)
    }

    fetchError()
  }, [])

  const handleRefetch = (deal: Deal) => async() => {
    const request = checkoutError(paymentOrderId, checkoutId)
    setLoad(request)
    const {reason} = await request
    if (reason === CheckoutError.NotInError) {
      const finalise = redemptionFlow.paymentComplete(deal, checkoutId, paymentOrderId)
      setLoad(finalise)
      const details = await finalise
      const url = detailsToWebAddress(details)
      return history.replace(url, context)
    }
    setError(reason)
  }

  return (
    <DealByUrlSlug variables={{urlSlug, isRewards: props.isRewards}} Error={RequireDealStatusOn}>
      {({data}) => {
        if (!data || !data.dealBySlug) return <RequireDealStatusOn deal={null} />
        const deal = data.dealBySlug

        return (
          <>
            <DealBanner deal={deal} />

            <Row marginBottom={4}>
              <Col mdOffset={3} md={6} xs={12}>
                <Card>
                  <Loading load={load}>
                    {error === CheckoutError.PaymentError && <PaymentError />}
                    {error === CheckoutError.StockAssignmentError && <StockAssignmentError />}
                    {error === CheckoutError.RedemptionError && <RedemptionError />}
                    {error === CheckoutError.UnknownError && <UnknownError onRetry={handleRefetch(deal)} />}

                    <ButtonRouterLink primary to={`/info/${deal.urlSlug}`}>
                      <FormattedMessage id="perks.components.paymentError.button" />
                    </ButtonRouterLink>
                  </Loading>
                </Card>
              </Col>
            </Row>
            <DealSuccessFinished />
          </>
        )
      }}
    </DealByUrlSlug>
  )
}

DealRedemptionError.contextTypes = {
  appPath: PropTypes.string
}

export default withPerkOrReward(DealRedemptionError)
